import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import PageMeta from "../components/PageMeta";
import LangToggle, { DraftNote } from "../components/LangToggle";
import useLang from "../lib/lang";
import { REVIEWED_DATES, formatReviewedDate } from "../lib/reviewed";

const OVERLINE = "text-[10px] uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const H2 = "font-serif text-lg md:text-xl text-ink leading-tight tracking-tight mb-2 mt-4";
const CARD = "border border-rule bg-white p-4 print:p-3 print:border-black";

function Row({ left, right }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 py-1.5 border-b border-rule/60 last:border-b-0 text-[13px] leading-snug">
      <div className="text-ink font-accent font-semibold">{left}</div>
      <div className="text-ink-muted">{right}</div>
    </div>
  );
}

const L = {
  en: {
    back: "Back to the full guide",
    print: "Print or save as PDF",
    overline: "Cheat sheet · Getting your records",
    title: "Your file, your rights — one page.",
    region: "England & Wales",
    disclaimer:
      "General information, not legal advice. If you are in live proceedings, ask your solicitor before making a request. Legal aid for parents in care proceedings is free and non-means-tested.",
    route1: "Route 1",
    sarTitle: "Subject Access Request (SAR)",
    sarBody: "Your right to see the personal data an organisation holds about you. UK GDPR Art. 15 & DPA 2018.",
    get: "Get",
    cost: "Cost",
    deadline: "Deadline",
    how: "How",
    sarGet: "Your file: case notes, chronologies, assessments, emails about you, meeting minutes.",
    sarCost: "Free.",
    sarDeadline: "1 calendar month (may extend +2 months if complex — they must tell you within month one).",
    sarHow: "Plain email: \u201cThis is a subject access request for all personal data you hold about me.\u201d",
    route2: "Route 2",
    foiTitle: "Freedom of Information (FOI)",
    foiBody: "Anyone\u2019s right to see recorded, non-personal information a public authority holds. FOI Act 2000.",
    foiGet: "Policies, thresholds, procedures, panel terms, spend, training material, contracts.",
    foiCost: "Free (in almost all cases).",
    foiDeadline: "20 working days.",
    foiHow: "Plain email marked \u201cFreedom of Information Request\u201d naming what you want.",
    sendBoth: (
      <>
        <strong>Send both, separately labelled, on the same day.</strong> A SAR will not get you policies. An FOI will not get you your file. Serious requesters use both.
      </>
    ),
    postureOverline: "The posture that works",
    posture: (
      <>
        Not &ldquo;they must give me everything.&rdquo; But: <span className="not-italic">every withholding decision must be justified, document by document, against a specific legal test — and you are entitled to ask which test, and why.</span>
      </>
    ),
    exemptTitle: "Common exemptions — and their limits",
    exemptItems: [
      <><strong className="text-ink">Third-party data:</strong> may redact <em>lines about others</em>, not whole documents. Professionals&rsquo; names &amp; opinions in their working role are usually <em>not</em> protected.</>,
      <><strong className="text-ink">Serious harm (DPA 2018 Sch. 3):</strong> narrow — must be a real risk of serious physical/mental harm, not embarrassment.</>,
      <><strong className="text-ink">Court records (FOIA s.32):</strong> covers material held <em>only because</em> it is on a court file. The underlying record still counts.</>,
    ],
    privTitle: "\u201cIt\u2019s privileged\u201d — the limits",
    privItems: [
      <>Attaches to <strong className="text-ink">documents</strong>, not whole departments.</>,
      <>Only communications for the <strong className="text-ink">dominant purpose</strong> of giving/receiving legal advice.</>,
      <>Facts don&rsquo;t become privileged by being told to a lawyer.</>,
      <>Sharing with third parties can <strong className="text-ink">waive</strong> it.</>,
      <>In children&rsquo;s proceedings, <em>litigation</em> privilege is narrower — <em>Re L</em> [1997] AC 16.</>,
      <>Under FOIA s.42 it is <strong className="text-ink">qualified</strong>: public interest test applies.</>,
    ],
    missTitle: "If they miss the deadline — or refuse",
    steps: [
      {
        overline: "Step 1",
        title: "Go back to them",
        body: <>FOI → ask for an <strong className="text-ink">internal review</strong>. SAR → complain to their Data Protection Officer. Be specific about which parts and which exemptions.</>,
      },
      {
        overline: "Step 2",
        title: "The ICO",
        body: <><a href="https://ico.org.uk/make-a-complaint" className="underline">ico.org.uk/make-a-complaint</a> · 0303 123 1113. Send your chronology. Cases take months.</>,
      },
      {
        overline: "Step 3",
        title: "Tribunal (FOI only)",
        body: <>Appeal an ICO decision to the First-tier Tribunal (Information Rights). Free. Decided afresh.</>,
      },
    ],
    oneQTitle: "The one question to ask when something is withheld",
    oneQ: "\u201cUnder which specific statutory exemption, applied to which specific document, and on what reasoning?\u201d",
    oneQNote: "You are entitled to an answer. The answer is often where your case starts.",
    cautionsTitle: "Two cautions",
    cautions: [
      <><strong className="text-ink">SARs are not disclosure.</strong> If you need a document for a hearing, ask your solicitor to apply for specific disclosure — much faster.</>,
      <><strong className="text-ink">Care what you publish.</strong> Section 12 Administration of Justice Act 1960 and FPR r.12.73 restrict publishing details of private children proceedings — including online.</>,
    ],
    todayTitle: "Do this today",
    today: [
      <>Send one <strong className="text-ink">SAR</strong>, one <strong className="text-ink">FOI</strong>, separately labelled.</>,
      <>Name the departments (incl. Legal Services).</>,
      <>Start a one-page chronology — dates down the left.</>,
      <>Diarise the two deadlines (1 month · 20 working days).</>,
      <>Consider sending the same pair to Cafcass.</>,
    ],
    reviewed: "Last reviewed",
    footerLink: "Read the full guide: truth4parents.com/understanding/records",
  },
  cy: {
    back: "Yn ôl i'r canllaw llawn",
    print: "Argraffu neu gadw fel PDF",
    overline: "Crynodeb un dudalen · Cael eich cofnodion",
    title: "Eich ffeil, eich hawliau — un dudalen.",
    region: "Cymru a Lloegr",
    disclaimer:
      "Gwybodaeth gyffredinol, nid cyngor cyfreithiol. Os ydych mewn achos byw, gofynnwch i'ch cyfreithiwr cyn gwneud cais. Mae cymorth cyfreithiol i rieni mewn achosion gofal yn rhad ac am ddim, heb brawf modd.",
    route1: "Llwybr 1",
    sarTitle: "Cais i weld eich data (SAR)",
    sarBody: "Eich hawl i weld y data personol y mae sefydliad yn ei ddal amdanoch. Erthygl 15 GDPR y DU a Deddf Diogelu Data 2018.",
    get: "Cewch",
    cost: "Cost",
    deadline: "Terfyn amser",
    how: "Sut",
    sarGet: "Eich ffeil: nodiadau achos, cronolegau, asesiadau, e-byst amdanoch, cofnodion cyfarfodydd.",
    sarCost: "Am ddim.",
    sarDeadline: "1 mis calendr (gellir ymestyn +2 fis os yw'n gymhleth — rhaid iddynt ddweud wrthych o fewn y mis cyntaf).",
    sarHow: "E-bost plaen: \u201cThis is a subject access request for all personal data you hold about me.\u201d (Gallwch ofyn yn Gymraeg neu'n Saesneg.)",
    route2: "Llwybr 2",
    foiTitle: "Rhyddid Gwybodaeth (FOI)",
    foiBody: "Hawl unrhyw un i weld gwybodaeth gofnodedig, amhersonol y mae awdurdod cyhoeddus yn ei dal. Deddf Rhyddid Gwybodaeth 2000.",
    foiGet: "Polisïau, trothwyon, gweithdrefnau, telerau paneli, gwariant, deunydd hyfforddi, contractau.",
    foiCost: "Am ddim (bron ym mhob achos).",
    foiDeadline: "20 diwrnod gwaith.",
    foiHow: "E-bost plaen wedi'i farcio \u201cFreedom of Information Request\u201d yn enwi'r hyn rydych ei eisiau.",
    sendBoth: (
      <>
        <strong>Anfonwch y ddau, wedi'u labelu ar wahân, ar yr un diwrnod.</strong> Ni chewch bolisïau drwy SAR. Ni chewch eich ffeil drwy FOI. Mae ceiswyr o ddifrif yn defnyddio'r ddau.
      </>
    ),
    postureOverline: "Yr agwedd sy'n gweithio",
    posture: (
      <>
        Nid &ldquo;rhaid iddyn nhw roi popeth i mi.&rdquo; Ond: <span className="not-italic">rhaid cyfiawnhau pob penderfyniad i atal gwybodaeth, ddogfen wrth ddogfen, yn erbyn prawf cyfreithiol penodol — ac mae gennych hawl i ofyn pa brawf, a pham.</span>
      </>
    ),
    exemptTitle: "Eithriadau cyffredin — a'u terfynau",
    exemptItems: [
      <><strong className="text-ink">Data trydydd parti:</strong> caniateir cuddio <em>llinellau am bobl eraill</em>, nid dogfennau cyfan. Nid yw enwau a barn gweithwyr proffesiynol yn eu rôl waith fel arfer wedi'u diogelu.</>,
      <><strong className="text-ink">Niwed difrifol (DPA 2018 Atodlen 3):</strong> cul — rhaid bod risg wirioneddol o niwed corfforol/meddyliol difrifol, nid embaras.</>,
      <><strong className="text-ink">Cofnodion llys (FOIA a.32):</strong> yn cwmpasu deunydd a ddelir <em>dim ond am ei fod</em> ar ffeil llys. Mae'r cofnod gwreiddiol yn dal i gyfrif.</>,
    ],
    privTitle: "\u201cMae'n freintiedig\u201d — y terfynau",
    privItems: [
      <>Yn perthyn i <strong className="text-ink">ddogfennau</strong>, nid adrannau cyfan.</>,
      <>Dim ond cyfathrebiadau at <strong className="text-ink">brif ddiben</strong> rhoi/derbyn cyngor cyfreithiol.</>,
      <>Nid yw ffeithiau'n dod yn freintiedig drwy eu dweud wrth gyfreithiwr.</>,
      <>Gall rhannu â thrydydd parti <strong className="text-ink">ildio'r</strong> fraint.</>,
      <>Mewn achosion plant, mae braint <em>ymgyfreitha</em> yn gulach — <em>Re L</em> [1997] AC 16.</>,
      <>O dan FOIA a.42 mae'n <strong className="text-ink">amodol</strong>: mae prawf budd y cyhoedd yn berthnasol.</>,
    ],
    missTitle: "Os ydyn nhw'n methu'r terfyn amser — neu'n gwrthod",
    steps: [
      {
        overline: "Cam 1",
        title: "Ewch yn ôl atynt",
        body: <>FOI → gofynnwch am <strong className="text-ink">adolygiad mewnol</strong>. SAR → cwynwch i'w Swyddog Diogelu Data. Byddwch yn benodol ynghylch pa rannau a pha eithriadau.</>,
      },
      {
        overline: "Cam 2",
        title: "Yr ICO",
        body: <><a href="https://ico.org.uk/make-a-complaint" className="underline">ico.org.uk/make-a-complaint</a> · 0303 123 1113. Anfonwch eich cronoleg. Gall achosion gymryd misoedd.</>,
      },
      {
        overline: "Cam 3",
        title: "Tribiwnlys (FOI yn unig)",
        body: <>Apeliwch yn erbyn penderfyniad yr ICO i'r Tribiwnlys Haen Gyntaf (Hawliau Gwybodaeth). Am ddim. Penderfynir o'r newydd.</>,
      },
    ],
    oneQTitle: "Yr un cwestiwn i'w ofyn pan gaiff rhywbeth ei atal",
    oneQ: "\u201cO dan ba eithriad statudol penodol, wedi'i gymhwyso i ba ddogfen benodol, ac ar ba resymeg?\u201d",
    oneQNote: "Mae gennych hawl i ateb. Yr ateb, yn aml, yw lle mae eich achos yn dechrau.",
    cautionsTitle: "Dau rybudd",
    cautions: [
      <><strong className="text-ink">Nid datgelu yw SAR.</strong> Os oes angen dogfen arnoch ar gyfer gwrandawiad, gofynnwch i'ch cyfreithiwr wneud cais am ddatgeliad penodol — llawer cyflymach.</>,
      <><strong className="text-ink">Byddwch yn ofalus beth rydych yn ei gyhoeddi.</strong> Mae adran 12 Deddf Gweinyddu Cyfiawnder 1960 a FPR r.12.73 yn cyfyngu ar gyhoeddi manylion achosion plant preifat — gan gynnwys ar-lein.</>,
    ],
    todayTitle: "Gwnewch hyn heddiw",
    today: [
      <>Anfonwch un <strong className="text-ink">SAR</strong>, un <strong className="text-ink">FOI</strong>, wedi'u labelu ar wahân.</>,
      <>Enwch yr adrannau (gan gynnwys y Gwasanaethau Cyfreithiol).</>,
      <>Dechreuwch gronoleg un dudalen — dyddiadau i lawr yr ochr chwith.</>,
      <>Rhowch y ddau derfyn amser yn eich dyddiadur (1 mis · 20 diwrnod gwaith).</>,
      <>Ystyriwch anfon yr un pâr at Cafcass.</>,
    ],
    reviewed: "Adolygwyd ddiwethaf",
    footerLink: "Darllenwch y canllaw llawn: truth4parents.com/understanding/records",
  },
};

export default function RecordsCheatSheetPage() {
  const [lang, setLang] = useLang();
  const t = L[lang] || L.en;

  return (
    <div data-testid="records-cheat-sheet-page" className="cheat-sheet">
      <PageMeta
        title="Records cheat sheet — one-page summary"
        description="A one-page printable summary of your rights to your records: subject access, freedom of information, common exemptions, deadlines, and how to complain to the ICO."
        path="/understanding/records/cheat-sheet"
      />

      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-linen-alt border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/understanding/records"
            data-testid="cheat-sheet-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            {t.back}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <LangToggle lang={lang} setLang={setLang} />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="cheat-sheet-print-button"
              className="inline-flex items-center gap-2 bg-river text-linen px-5 py-2.5 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
            >
              <Printer size={14} strokeWidth={1.75} />
              {t.print}
            </button>
          </div>
        </div>
      </div>

      {/* Sheet — sized for A4 */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 print:p-0 print:max-w-full">
        <article className="bg-white border border-rule print:border-0 p-8 md:p-10 print:p-6 text-[13px] leading-snug text-ink-muted">
          {/* Header band */}
          <header className="flex items-end justify-between gap-6 border-b-2 border-ember pb-3 mb-4">
            <div>
              <div className={OVERLINE}>{t.overline}</div>
              <h1 className="font-serif text-2xl md:text-3xl text-ink tracking-tight mt-1 leading-none">
                {t.title}
              </h1>
            </div>
            <div className="text-right text-[10px] text-ink-muted font-accent">
              <div>truth4parents.com</div>
              <div className="mt-0.5">{t.region}</div>
            </div>
          </header>

          <DraftNote lang={lang} />

          <p className="text-[12px] italic text-ink-muted mb-4 mt-3">{t.disclaimer}</p>

          {/* Grid of two: SAR vs FOI */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-3">
            <div className={CARD}>
              <div className={OVERLINE}>{t.route1}</div>
              <h2 className={H2}>{t.sarTitle}</h2>
              <p className="mb-2">{t.sarBody}</p>
              <Row left={t.get} right={t.sarGet} />
              <Row left={t.cost} right={t.sarCost} />
              <Row left={t.deadline} right={t.sarDeadline} />
              <Row left={t.how} right={t.sarHow} />
            </div>
            <div className={CARD}>
              <div className={OVERLINE}>{t.route2}</div>
              <h2 className={H2}>{t.foiTitle}</h2>
              <p className="mb-2">{t.foiBody}</p>
              <Row left={t.get} right={t.foiGet} />
              <Row left={t.cost} right={t.foiCost} />
              <Row left={t.deadline} right={t.foiDeadline} />
              <Row left={t.how} right={t.foiHow} />
            </div>
          </div>

          <p className="mt-3 text-[12px] text-ink">{t.sendBoth}</p>

          {/* Posture */}
          <div className="mt-5 border-l-4 border-ember pl-4">
            <div className={OVERLINE}>{t.postureOverline}</div>
            <p className="font-serif text-ink italic text-base leading-snug mt-1">{t.posture}</p>
          </div>

          {/* Two column: exemptions & privilege */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5 print:grid-cols-2 print:gap-3">
            <div className={CARD}>
              <h2 className={H2}>{t.exemptTitle}</h2>
              <ul className="space-y-1.5 text-[12.5px]">
                {t.exemptItems.map((item, i) => (
                  <li key={`ex-${i}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={CARD}>
              <h2 className={H2}>{t.privTitle}</h2>
              <ul className="space-y-1.5 text-[12.5px]">
                {t.privItems.map((item, i) => (
                  <li key={`pr-${i}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Escalation ladder */}
          <div className="mt-5">
            <h2 className={H2}>{t.missTitle}</h2>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12.5px] print:grid-cols-3 print:gap-2">
              {t.steps.map((s) => (
                <li key={s.overline} className={`${CARD} !p-3`}>
                  <div className={OVERLINE}>{s.overline}</div>
                  <div className="font-serif text-ink text-base mt-1 mb-1">{s.title}</div>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* One-line question */}
          <div className="mt-5 bg-linen-alt border-l-4 border-river p-4 print:bg-white print:border-l-2 print:border-black">
            <h2 className={H2}>{t.oneQTitle}</h2>
            <p className="font-serif text-ink italic text-base leading-snug">{t.oneQ}</p>
            <p className="mt-2 text-[12px]">{t.oneQNote}</p>
          </div>

          {/* Cautions */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-3">
            <div className={CARD}>
              <h2 className={H2}>{t.cautionsTitle}</h2>
              <ul className="space-y-1.5 text-[12.5px]">
                {t.cautions.map((item, i) => (
                  <li key={`c-${i}`}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={CARD}>
              <h2 className={H2}>{t.todayTitle}</h2>
              <ul className="space-y-1.5 text-[12.5px]">
                {t.today.map((item, i) => (
                  <li key={`d-${i}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <footer className="mt-6 pt-3 border-t border-rule text-[10px] text-ink-muted flex justify-between items-center">
            <div>© You Are Not Alone · truth4parents.com · {t.reviewed} <time dateTime={REVIEWED_DATES["/understanding/records/cheat-sheet"]}>{formatReviewedDate(REVIEWED_DATES["/understanding/records/cheat-sheet"])}</time></div>
            <div>{t.footerLink}</div>
          </footer>
        </article>
      </div>
    </div>
  );
}
